import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, List, ListItem, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { callAPISync, updateCustomData } from '../../utils/api';
import { updateUserData } from '../../store/system/systemSlice';
import { useLogto } from '@logto/react';

const TOUDialog: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const userCustomData = useAppSelector((state) => state.system);

	const updateCustomDataUtil = (touAgreed: boolean) => {
		dispatch(updateUserData({ touAgreed: touAgreed }));
		callAPISync(logto, (token) => {
			updateCustomData(token, { touAgreed: touAgreed });
		});
	};

	const getLegacyShow = (): boolean => {
		const legacyAgreed = localStorage.getItem('tou_agreed');
		updateCustomDataUtil(legacyAgreed === 'true' ? true : false);

		return legacyAgreed !== 'true';
	};

	// start true to avoid users bypassing
	const [showDialog, setShowDialog] = useState<boolean>(
		userCustomData.touAgreed === undefined ? getLegacyShow() : !userCustomData.touAgreed
	);

	const contactEmail: string = import.meta.env.VITE_CONTACT_EMAIL || '';

	return (
		<Dialog open={showDialog} sx={{ backdropFilter: 'blur(5px)' }}>
			<DialogTitle textAlign={'center'}>PROMETHEUS System Terms of Use</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={2}>
					<Typography>
						By using this system you are responsible for ensuring that you comply with all applicable policies specified by your Law
						Enforcement Agency (LEA) and all applicable laws. All user activity is monitored and logged, and is subject to auditing.
						Unauthorised use of this system is prohibited and subject to disciplinary procedures.
					</Typography>
					<Typography>
						All users have been provided with individual credentials - sharing of credentials and/or logon sessions are not permitted. You
						will be held responsible for all actions carried out under your login session.
					</Typography>
					<Typography>
						You must immediately report any suspected or actual compromise of a password or other security incident to{' '}
						<Link aria-label={`Link to the contact email: ${contactEmail}`} href={`mailto:${contactEmail}`}>
							{contactEmail}
						</Link>
					</Typography>
					<Typography>Users must ensure that they:</Typography>
					<List>
						<ListItem>
							Do not accidentally or deliberately transfer any sensitive material to systems that are not approved/accredited for such
							material, or different Internet services used.
						</ListItem>
						<ListItem>
							Do not attempt to alter the configuration of any security settings or otherwise attempt to gain unauthorised privileges.
						</ListItem>
						<ListItem>Follow any specific LEA security requirements</ListItem>
						<ListItem>Only upload data to this platform that follows the relevant legal requirements</ListItem>
						<ListItem>Only access the platform using corporately provisioned devices, complaint with cyber risk profile</ListItem>
					</List>
					<Typography>
						For a complete set of policies and further details of system operation, please contact your designated Force Lead.
					</Typography>
					<Typography>
						You also consent to the storage of limited and only essential cookies, and other data required for the system to function, on
						your device. Finally, you consent to limited contact via your registered email address solely for the purposes of providing
						updates on this system - these may include update, outage, or critical issue notices.
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					fullWidth
					size="large"
					onClick={() => {
						updateCustomDataUtil(true);
						setShowDialog(false);
					}}
					data-testid="tou-confirm-button"
				>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TOUDialog;
